/**
 * Las reglas del juego, como funciones puras.
 *
 * Por que estan aqui y no dentro del motor: el motor necesita WebGL, un canvas
 * y requestAnimationFrame, asi que solo se puede probar abriendo un navegador.
 * Y probar a mano en un navegador es justo lo que deja pasar los fallos que
 * importan: el 30-sep-2026, jugando, se vio que el nivel mataba al jugador a los
 * 4 metros y que tocar para cambiarse de carril te mataba con lo que hubiera en
 * el carril de destino antes de llegar. Dos reglas mal puestas, invisibles para
 * cualquier test que mirase el render.
 *
 * Todo lo de este fichero se prueba con `npm test`, sin navegador.
 */

import cfg from '@compartido/nivel-config.json';

// Todos estos numeros vienen de `shared/nivel-config.json`, que leen el juego Y
// el servidor. Estaban escritos aqui a mano, y el servidor los necesita para
// poder REPETIR una partida de duelo y comprobar que los movimientos declarados
// llevan de verdad a la marca declarada. Con dos copias, el dia que una cambie
// el servidor empezaria a rechazar duelos honestos.
export const ALTO_JUGADOR = cfg.jugador.altoNormal;
export const ALTO_DESLIZANDO = cfg.jugador.altoDeslizando;

/**
 * Medio ancho de la zona de choque, en unidades del mundo.
 *
 * El carril mide 2,2, asi que medio carril serian 1,1. Se usa 0,86 a proposito:
 * en un movil el dedo del jugador ya introduce bastante error, y cuando la duda
 * esta entre perdonar y castigar, en un runner se perdona. Un jugador que siente
 * que murio injustamente deja de jugar; uno que siente que se salvo por poco,
 * juega otra vez.
 */
export const MEDIO_ANCHO_CHOQUE = cfg.jugador.medioAnchoChoque;

/** Ventana en z dentro de la cual una pieza esta "a la altura" del jugador. */
export const Z_CHOQUE_MIN = -1.5;
export const Z_CHOQUE_MAX = 1.3;

export type TipoObstaculo = 'barrera' | 'vela' | 'tiburon';

export interface EstadoJugador {
  /** Posicion lateral REAL, no el carril al que apunta. */
  x: number;
  /** Altura sobre el suelo. */
  y: number;
  /** Si va deslizandose ahora mismo. */
  deslizando: boolean;
  /** Segundos que le quedan al cohete. Con cohete se pasa por encima de todo. */
  tCohete: number;
}

/** Alto efectivo del jugador: deslizandose ocupa mucho menos. */
export function altoEfectivo(deslizando: boolean): number {
  return deslizando ? ALTO_DESLIZANDO : ALTO_JUGADOR;
}

/**
 * Si una pieza esta lo bastante cerca en z para que importe.
 * Aparte de la logica, es lo que evita comprobar cien piezas por cuadro.
 */
export function enRangoZ(zMundo: number): boolean {
  return zMundo >= Z_CHOQUE_MIN && zMundo <= Z_CHOQUE_MAX;
}

/**
 * Si el jugador y la pieza coinciden lateralmente.
 *
 * Se mide con la x REAL del jugador, no con el carril al que apunto. El carril
 * es una intencion; la x es un hecho. Mezclarlos mata al jugador en cuanto toca
 * la pantalla, con el personaje todavia a dos metros del carril nuevo, y desde
 * fuera eso no tiene ninguna explicacion.
 */
export function coincidenEnX(xJugador: number, xPieza: number): boolean {
  return Math.abs(xPieza - xJugador) < MEDIO_ANCHO_CHOQUE;
}

/**
 * Si el jugador libro el obstaculo.
 *
 *  - barrera: se SALTA. Hay que estar por encima de su alto.
 *  - vela:    flota a la altura del pecho. Se pasa por DEBAJO deslizandose,
 *             o por encima si se va muy alto (con cohete, por ejemplo).
 *  - tiburon: ocupa el carril de arriba abajo. No hay forma de librarlo: hay
 *             que CAMBIARSE de carril. De aqui sale la invariante del generador
 *             de nivel, que nunca cierra los tres carriles en la misma ranura.
 */
export function obstaculoLibrado(
  tipo: TipoObstaculo,
  jugador: EstadoJugador
): boolean {
  // Con el cohete se vuela por encima de todo.
  if (jugador.tCohete > 0) return true;
  if (tipo === 'barrera') return jugador.y > cfg.jugador.alturaParaLibrarBarrera;
  if (tipo === 'vela')
    return jugador.deslizando || jugador.y > cfg.jugador.alturaParaLibrarVela;
  // tiburon
  return false;
}

/**
 * La decision completa: si esta pieza choca con el jugador.
 * Es la funcion que usa el motor, y la que se prueba.
 */
export function hayChoque(
  tipo: TipoObstaculo,
  zMundo: number,
  xPieza: number,
  jugador: EstadoJugador
): boolean {
  if (!enRangoZ(zMundo)) return false;
  if (!coincidenEnX(jugador.x, xPieza)) return false;
  return !obstaculoLibrado(tipo, jugador);
}

/**
 * Si el jugador puede saltar ahora.
 * Solo desde el suelo y sin estar deslizandose: sin esta regla el juego seria
 * de volar, no de correr.
 */
export function puedeSaltar(y: number, deslizando: boolean): boolean {
  return y <= 0.01 && !deslizando;
}

/**
 * Si se recoge un recolectable. Mas permisivo que un choque en la altura:
 * dejar escapar una moneda que el jugador creia tener cogida se siente mal, y
 * aqui equivocarse no cuesta una partida.
 */
export function hayRecogida(
  zMundo: number,
  xPieza: number,
  yPieza: number,
  jugador: EstadoJugador
): boolean {
  if (!enRangoZ(zMundo)) return false;
  if (!coincidenEnX(jugador.x, xPieza)) return false;
  if (jugador.tCohete > 0) return true;
  return Math.abs(yPieza - (jugador.y + 0.8)) < 1.5;
}
